import { c as _c } from "react/compiler-runtime";
import { useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useAppRouter } from '@hooks/navigation';
import { useContactRouter } from '@hooks/useContactRouter';
import { useCurrentUser } from '@hooks/useCurrentUser';
import { useHasPermission } from '@hooks/useHasPermission';
import { AppRouteEnum, CallDirectionEnum, PersistedStateKeyEnum, QueryKeyEnum, TransferTypeEnum } from '@lib/enums';
import { LocaleEnum } from '@lib/enums/LocaleEnum';
import { useJotaiAtom, useJotaiValue } from '@states/jotai/hooks';
import { LocaleAtom } from '@states/localeAtom';
import { contactSelector } from '@states/softphone/contactSelector';
import { currentCallAtom, INITIAL_CURRENT_CALL_STATE } from '@states/softphone/currentCallAtom';
export type TUseCallRedirectReturn = {
  performCallRedirect: () => void;
};
export function useCallRedirect() {
  const $ = _c(34);
  const queryClient = useQueryClient();
  const router = useAppRouter();
  const {
    isCustomerService,
    isRetention
  } = useCurrentUser();
  const [, setLocale] = useJotaiAtom(LocaleAtom);
  const [t0, setCurrentCallState] = useJotaiAtom(currentCallAtom);
  const {
    lastTouchedQuoteId
  } = t0;
  const {
    contactId,
    contactMasterId,
    callDirection,
    transferQuoteId,
    transferHomeQuoteId,
    transferContractId,
    transferConsumerId,
    transferType,
    isDialerReturn,
    isInternetResponder,
    isTvResponder,
    isSpanishSkillActive
  } = useJotaiValue(contactSelector);
  const {
    goToSearchPageForCurrentContact,
    goToContactPreview,
    goToExternalTransfer,
    goToQuoteFlowEntry,
    goToHomeQuoteFlowEntry,
    goToConsumerProfile,
    goToMoxyCustomerNumberSearch,
    goToContractDetails
  } = useContactRouter();
  let t1;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      key: "quotes:read"
    };
    t2 = {
      key: "home-quotes:read"
    };
    $[0] = t1;
    $[1] = t2;
  } else {
    t1 = $[0];
    t2 = $[1];
  }
  const [canReadQuotes, canReadHomeQuotes] = useHasPermission(t1, t2);
  let t3;
  if ($[2] !== callDirection || $[3] !== canReadHomeQuotes || $[4] !== canReadQuotes || $[5] !== contactId || $[6] !== contactMasterId || $[7] !== goToConsumerProfile || $[8] !== goToContactPreview || $[9] !== goToContractDetails || $[10] !== goToExternalTransfer || $[11] !== goToHomeQuoteFlowEntry || $[12] !== goToMoxyCustomerNumberSearch || $[13] !== goToQuoteFlowEntry || $[14] !== goToSearchPageForCurrentContact || $[15] !== isCustomerService || $[16] !== isDialerReturn || $[17] !== isInternetResponder || $[18] !== isRetention || $[19] !== isSpanishSkillActive || $[20] !== isTvResponder || $[21] !== lastTouchedQuoteId || $[22] !== queryClient || $[23] !== router || $[24] !== setCurrentCallState || $[25] !== setLocale || $[26] !== transferConsumerId || $[27] !== transferContractId || $[28] !== transferHomeQuoteId || $[29] !== transferQuoteId || $[30] !== transferType) {
    t3 = () => {
      queryClient.invalidateQueries({
        queryKey: [QueryKeyEnum.Consumer],
        refetchType: "none"
      });
      queryClient.invalidateQueries({
        queryKey: [QueryKeyEnum.Vehicle],
        refetchType: "none"
      });
      queryClient.invalidateQueries({
        queryKey: [QueryKeyEnum.VehicleQuote],
        refetchType: "none"
      });
      queryClient.invalidateQueries({
        queryKey: [QueryKeyEnum.HomeQuote],
        refetchType: "none"
      });
      queryClient.invalidateQueries({
        queryKey: [QueryKeyEnum.CallDetails],
        refetchType: "none"
      });
      localStorage.removeItem(PersistedStateKeyEnum.SearchType);
      localStorage.removeItem(PersistedStateKeyEnum.ManualSearchFormState);
      localStorage.removeItem(PersistedStateKeyEnum.DirectMailSearchType);
      localStorage.removeItem(PersistedStateKeyEnum.DirectMailSearchFormState);
      localStorage.removeItem(PersistedStateKeyEnum.SearchMoxyCustomerNumberFormState);
      setCurrentCallState(() => ({
        ...INITIAL_CURRENT_CALL_STATE,
        lastRoutedContactId: contactId,
        lastRoutedContactMasterId: contactMasterId,
        lastTouchedQuoteId
      }));
      setLocale({
        locale: isSpanishSkillActive === true ? LocaleEnum.ES_MX : LocaleEnum.EN_US
      });
      if (transferContractId != null) {
        goToContractDetails(transferContractId);
        return;
      }
      if (transferHomeQuoteId != null && canReadHomeQuotes) {
        goToHomeQuoteFlowEntry(transferHomeQuoteId);
        return;
      }
      if (transferQuoteId != null && canReadQuotes) {
        goToQuoteFlowEntry(transferQuoteId);
        return;
      }
      if (transferConsumerId != null) {
        goToConsumerProfile(transferConsumerId);
        return;
      }
      if (isCustomerService) {
        router.build(AppRouteEnum.Dashboard).go();
        return;
      }
      if (isRetention) {
        goToSearchPageForCurrentContact();
        return;
      }
      if (transferType != null) {
        transferType === TransferTypeEnum.External ? goToExternalTransfer() : goToMoxyCustomerNumberSearch();
        return;
      }
      if (isDialerReturn === true) {
        router.build(AppRouteEnum.ContactSearchAuto).go();
        return;
      }
      if (isInternetResponder === true || isTvResponder === true) {
        router.build(AppRouteEnum.ContactSearchMail).go();
        return;
      }
      if (callDirection === CallDirectionEnum.Outbound) {
        goToContactPreview();
        return;
      }
      goToSearchPageForCurrentContact();
    };
    $[2] = callDirection;
    $[3] = canReadHomeQuotes;
    $[4] = canReadQuotes;
    $[5] = contactId;
    $[6] = contactMasterId;
    $[7] = goToConsumerProfile;
    $[8] = goToContactPreview;
    $[9] = goToContractDetails;
    $[10] = goToExternalTransfer;
    $[11] = goToHomeQuoteFlowEntry;
    $[12] = goToMoxyCustomerNumberSearch;
    $[13] = goToQuoteFlowEntry;
    $[14] = goToSearchPageForCurrentContact;
    $[15] = isCustomerService;
    $[16] = isDialerReturn;
    $[17] = isInternetResponder;
    $[18] = isRetention;
    $[19] = isSpanishSkillActive;
    $[20] = isTvResponder;
    $[21] = lastTouchedQuoteId;
    $[22] = queryClient;
    $[23] = router;
    $[24] = setCurrentCallState;
    $[25] = setLocale;
    $[26] = transferConsumerId;
    $[27] = transferContractId;
    $[28] = transferHomeQuoteId;
    $[29] = transferQuoteId;
    $[30] = transferType;
    $[31] = t3;
  } else {
    t3 = $[31];
  }
  const performCallRedirect = t3;
  let t4;
  if ($[32] !== performCallRedirect) {
    t4 = {
      performCallRedirect
    };
    $[32] = performCallRedirect;
    $[33] = t4;
  } else {
    t4 = $[33];
  }
  return t4;
}